import React from "react";
import { useTranslation } from "react-i18next";
import useAnalyticsContext from "../../hooks/useAnalyticsContext.js";
import { trackSectionViewed, useSectionOnceObserver } from "../../lib/analytics";
import { DWI_SECTION } from "./sections.js";

/**
 * Questions about digital wedding invitations.
 *
 * A plain definition list, always expanded — same reasoning as the photo-sharing
 * FAQ: every answer is short enough to read in place, and these are the page's
 * most self-contained factual passages, which is exactly what an assistant
 * quotes. Putting them behind an accordion would hide them from a reader and
 * gain nothing from a crawler.
 *
 * Every answer is checked against the implementation. The four that are easiest
 * to get wrong, and what the code actually says:
 *
 *  - `change` — a guest replies ONCE. POST /rsvp/:code 409s on re-submission
 *    (`RSVP_ALREADY_SUBMITTED`); only the couple can amend an answer afterwards.
 *  - `private` — access control is an unguessable URL and nothing else. The
 *    endpoint is public and there is no password, so the answer says so plainly
 *    rather than implying authentication.
 *  - `companions` — guest-added people are stored `reviewStatus: "pendingReview"`
 *    and a self-registered guest is `pendingApproval`, invisible to headcounts
 *    until approved. "Nothing changes your numbers without you" is literal.
 *  - `website` — the ONE place on this page the phrase "wedding website" is
 *    allowed, because the question is the disambiguation. The forbidden-claims
 *    test pins that exception to this key.
 */
const FAQ_KEYS = [
  "what",
  "howWorks",
  "app",
  "account",
  "howRsvp",
  "companions",
  "perMoment",
  "change",
  "ownLink",
  "language",
  "website",
  "sample",
  "plan",
  "baptism",
  "private",
];

const InvitationFaq = () => {
  const { t } = useTranslation();
  const analyticsContext = useAnalyticsContext();

  const sectionRef = useSectionOnceObserver(() =>
    trackSectionViewed(DWI_SECTION.FAQ, analyticsContext),
  );

  return (
    <section ref={sectionRef} className="section topless wps-section">
      <div className="container">
        <div className="wps-section-header">
          <h2 className="heading-50">
            {t("digitalWeddingInvitation.faq.heading")}
          </h2>
        </div>

        <dl className="wps-faq">
          {FAQ_KEYS.map((key) => (
            <div key={key} className="wps-faq-item">
              <dt className="made-22 wps-faq-q">
                {t(`digitalWeddingInvitation.faq.items.${key}.q`)}
              </dt>
              <dd className="wps-faq-a body-15">
                {t(`digitalWeddingInvitation.faq.items.${key}.a`)}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
};

export default InvitationFaq;
